import { useState } from "react";
import { CheckCircle2, Search, ExternalLink, RefreshCcw } from "lucide-react";
import { SOCIAL, HUNT_CODE } from "@/lib/siteAssets";

/**
 * "Think you found the code?" mini-game.
 * Scavenger-hunt reward — customer finds the code on TikTok and validates
 * it here for a bit of feel-good gratification. Doesn't apply an actual
 * discount (the vending machine's coupon software does that). It just
 * confirms + celebrates.
 */
export default function CodeHunt() {
    const [input, setInput] = useState("");
    const [status, setStatus] = useState("idle"); // idle | wrong | correct
    const [attempts, setAttempts] = useState(0);

    const submit = (e) => {
        e.preventDefault();
        const cleaned = input.replace(/\D/g, ""); // digits only (system limitation)
        setAttempts((a) => a + 1);
        if (cleaned === HUNT_CODE) {
            setStatus("correct");
        } else {
            setStatus("wrong");
        }
    };

    const reset = () => {
        setInput("");
        setStatus("idle");
    };

    if (status === "correct") {
        return (
            <div
                className="relative overflow-hidden rounded-2xl border border-green-500/40 bg-gradient-to-br from-emerald-600 via-green-600 to-green-500 text-white p-5"
                data-testid="code-hunt-success"
            >
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-md bg-white/20 backdrop-blur flex items-center justify-center">
                        <CheckCircle2 size={16} />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-semibold">
                        Nice work 🕵️
                    </div>
                </div>
                <div className="text-lg font-semibold leading-tight mb-1.5">
                    You cracked the code.
                </div>
                <p className="text-sm text-white/90 leading-relaxed">
                    Bring this code to any Vendagain phone case machine and enter it
                    at checkout to get <b>$5 off your case</b>.
                </p>
                <div className="mt-3 flex items-center justify-between gap-3 bg-black/30 backdrop-blur rounded-lg px-3 py-2 font-mono">
                    <span className="text-2xl tracking-[0.15em] font-semibold">
                        {HUNT_CODE}
                    </span>
                    <button
                        onClick={() => navigator.clipboard?.writeText(HUNT_CODE)}
                        className="text-[11px] uppercase tracking-widest font-semibold text-white/80 hover:text-white"
                        data-testid="copy-code-btn"
                    >
                        Copy
                    </button>
                </div>
                <button
                    onClick={reset}
                    className="mt-3 text-xs text-white/70 hover:text-white flex items-center gap-1"
                >
                    <RefreshCcw size={11} /> Reset
                </button>
            </div>
        );
    }

    return (
        <div
            className="rounded-2xl border border-neutral-200 bg-white p-5"
            data-testid="code-hunt-card"
        >
            <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-md bg-neutral-950 text-green-400 flex items-center justify-center">
                    <Search size={15} />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">
                    Scavenger hunt
                </div>
            </div>
            <div className="text-base font-semibold leading-tight text-neutral-950 mb-1.5">
                Think you found the code?
            </div>
            <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                There's a secret code hidden on our TikTok that gets you{" "}
                <b>$5 off</b> a case. Watch, hunt, and enter it below to check.
            </p>

            <form onSubmit={submit} className="space-y-2">
                <input
                    type="text"
                    inputMode="numeric"
                    value={input}
                    onChange={(e) => {
                        setInput(e.target.value);
                        if (status === "wrong") setStatus("idle");
                    }}
                    placeholder="Enter the code"
                    className="vg-input font-mono tracking-[0.15em] text-center"
                    data-testid="hunt-input"
                    autoComplete="off"
                />
                <button
                    type="submit"
                    disabled={!input.trim()}
                    className="vg-pill vg-pill-primary w-full justify-center"
                    data-testid="hunt-submit"
                >
                    Check code
                </button>
            </form>

            {status === "wrong" && (
                <p
                    className="mt-3 text-xs text-red-600 text-center"
                    data-testid="hunt-wrong"
                >
                    {attempts > 2
                        ? "Still nope — the code lives on our TikTok. Keep hunting 👀"
                        : "Not quite. Try again — or head to the TikTok for a clue."}
                </p>
            )}

            <a
                href={SOCIAL.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center justify-center gap-1.5 text-xs font-semibold text-neutral-500 hover:text-neutral-900 transition-colors"
                data-testid="hunt-tiktok-link"
            >
                Watch @vendagain on TikTok
                <ExternalLink size={11} />
            </a>
        </div>
    );
}
